import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosInstance';
import { motion } from 'framer-motion';
import { FiCalendar, FiMapPin, FiLayers, FiPackage, FiSearch, FiCheck, FiArrowLeft, FiAlertCircle } from 'react-icons/fi';

export default function ScheduleAuctionCreate() {
  const navigate = useNavigate();
  
  // Form fields
  const [auctionDate, setAuctionDate] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCommodity, setSelectedCommodity] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [selectedScheme, setSelectedScheme] = useState('');

  // Dropdown options
  const [states, setStates] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [schemes, setSchemes] = useState([]);

  // Warehouse list loaded from inventory
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState(new Set());

  // Component states
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch initial dropdown options on load
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        setLoadingOptions(true);
        setError('');
        const [statesRes, commoditiesRes, seasonsRes, schemesRes] = await Promise.all([
          axios.get('states'),
          axios.get('commodity'),
          axios.get('seasons?paginate=false'),
          axios.get('schemes')
        ]);
        setStates(statesRes.data || []);
        setCommodities(commoditiesRes.data || []);
        setSeasons(seasonsRes.data || []);
        setSchemes(schemesRes.data || []);
      } catch (err) {
        console.error('Failed to load dropdown options:', err);
        setError('Failed to load dropdown options. Please refresh the page.');
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchDropdownOptions();
  }, []);

  // Fetch related warehouses when state, commodity, season, and scheme are all selected
  useEffect(() => {
    const fetchWarehouses = async () => {
      if (!selectedState || !selectedCommodity || !selectedSeason || !selectedScheme) {
        setWarehouses([]);
        setSelectedWarehouseIds(new Set());
        return;
      }

      try {
        setLoadingWarehouses(true);
        setError('');
        const res = await axios.get(`schedule-auction/eligible-warehouses`, {
          params: {
            state_id: selectedState,
            commodity_id: selectedCommodity,
            season_id: selectedSeason,
            scheme_id: selectedScheme
          }
        });
        setWarehouses(res.data || []);
        setSelectedWarehouseIds(new Set()); // reset selection
      } catch (err) {
        console.error('Failed to load eligible warehouses:', err);
        setError('Failed to load warehouses with current inventory.');
      } finally {
        setLoadingWarehouses(false);
      }
    };

    fetchWarehouses();
  }, [selectedState, selectedCommodity, selectedSeason, selectedScheme]);

  // Checkbox logic: individual toggle
  const handleToggleWarehouse = (id) => {
    setSelectedWarehouseIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Checkbox logic: master toggle
  const handleToggleAll = (e) => {
    if (e.target.checked) {
      const allIds = warehouses.map(w => w.warehouse_id);
      setSelectedWarehouseIds(new Set(allIds));
    } else {
      setSelectedWarehouseIds(new Set());
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!auctionDate) {
      setError('Please select an auction date.');
      return;
    }
    if (!selectedState || !selectedCommodity || !selectedSeason || !selectedScheme) {
      setError('Please select State, Commodity, Season, and Scheme.');
      return;
    }
    if (selectedWarehouseIds.size === 0) {
      setError('Please select at least one warehouse for this schedule.');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        auction_date: auctionDate,
        state_id: selectedState,
        commodity_id: selectedCommodity,
        season_id: selectedSeason,
        scheme_id: selectedScheme,
        warehouses: warehouses
          .filter(w => selectedWarehouseIds.has(w.warehouse_id))
          .map(w => ({
            warehouse_id: w.warehouse_id,
            current_quantity: w.current_quantity,
            current_bags: w.current_bags
          }))
      };

      await axios.post('schedule-auction/create', payload);
      setSuccess('Auction scheduled successfully!');
      
      setTimeout(() => {
        navigate('/schedule-auction');
      }, 1500);
    } catch (err) {
      console.error('Error scheduling auction:', err);
      setError(err.response?.data?.message || err.message || 'Failed to schedule auction.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-8 text-gray-900">
      
      {/* Alert boxes */}
      {error && (
        <div className="mb-6 flex items-center gap-2 p-4 bg-red-100 border border-red-300 text-red-700 rounded-xl text-base font-semibold">
          <FiAlertCircle className="text-xl" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 flex items-center gap-2 p-4 bg-green-100 border border-green-300 text-green-700 rounded-xl text-base font-semibold">
          <FiCheck className="text-xl" />
          {success}
        </div>
      )}

      {/* Back Button & Title */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/schedule-auction')}
          className="p-2 rounded-lg bg-white shadow hover:bg-gray-50 transition border border-gray-100"
          title="Back to List"
        >
          <FiArrowLeft size={20} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-blue-800 tracking-tight">
            Schedule a New Auction
          </h1>
          <p className="text-gray-600 text-sm mt-0.5">
            Fill in the details and choose warehouse receipts with current quantities.
          </p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        
        {/* Form Controls Card */}
        <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 grid grid-cols-1 md:grid-cols-5 gap-6 items-end">
          
          {/* Auction Date */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-600 mb-2 flex items-center gap-1.5">
              <FiCalendar className="text-blue-500" /> Auction Date
            </label>
            <input
              type="date"
              value={auctionDate}
              onChange={(e) => setAuctionDate(e.target.value)}
              className="border border-gray-300 p-3 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-full bg-gray-50/50"
              required
            />
          </div>

          {/* State Select */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-600 mb-2 flex items-center gap-1.5">
              <FiMapPin className="text-blue-500" /> State
            </label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="border border-gray-300 p-3 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-full bg-gray-50/50 cursor-pointer"
              required
              disabled={loadingOptions}
            >
              <option value="">Select State</option>
              {states.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Commodity Select */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-600 mb-2 flex items-center gap-1.5">
              <FiPackage className="text-blue-500" /> Commodity
            </label>
            <select
              value={selectedCommodity}
              onChange={(e) => setSelectedCommodity(e.target.value)}
              className="border border-gray-300 p-3 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-full bg-gray-50/50 cursor-pointer"
              required
              disabled={loadingOptions}
            >
              <option value="">Select Commodity</option>
              {commodities.map(c => (
                <option key={c.id} value={c.id}>{c.commodityname}</option>
              ))}
            </select>
          </div>

          {/* Season Select */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-600 mb-2 flex items-center gap-1.5">
              <FiLayers className="text-blue-500" /> Season
            </label>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="border border-gray-300 p-3 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-full bg-gray-50/50 cursor-pointer"
              required
              disabled={loadingOptions}
            >
              <option value="">Select Season</option>
              {seasons.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Scheme Select */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-600 mb-2 flex items-center gap-1.5">
              <FiLayers className="text-blue-500" /> Scheme
            </label>
            <select
              value={selectedScheme}
              onChange={(e) => setSelectedScheme(e.target.value)}
              className="border border-gray-300 p-3 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-full bg-gray-50/50 cursor-pointer"
              required
              disabled={loadingOptions}
            >
              <option value="">Select Scheme</option>
              {schemes.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

        </div>

        {/* Warehouses Selector Card */}
        {selectedState && selectedCommodity && selectedSeason && selectedScheme && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
              <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                <FiSearch className="text-blue-600" /> Available Warehouses Scoped from Inventory
              </h3>
              <span className="text-sm text-gray-500 font-semibold">
                {selectedWarehouseIds.size} of {warehouses.length} selected
              </span>
            </div>

            {loadingWarehouses ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : warehouses.length === 0 ? (
              <div className="text-center py-16 text-gray-500 font-medium">
                🚫 No warehouses with available stock found for this selection.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase w-[80px]">
                        <input
                          type="checkbox"
                          onChange={handleToggleAll}
                          checked={warehouses.length > 0 && selectedWarehouseIds.size === warehouses.length}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        />
                      </th>
                      <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">Warehouse Name</th>
                      <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">Warehouse Code</th>
                      <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase text-right">Current Quantity (MT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warehouses.map((w) => (
                      <tr
                        key={w.warehouse_id}
                        className={`hover:bg-gray-50 border-b border-gray-100 transition-colors duration-150 cursor-pointer ${
                          selectedWarehouseIds.has(w.warehouse_id) ? 'bg-blue-50/20' : ''
                        }`}
                        onClick={() => handleToggleWarehouse(w.warehouse_id)}
                      >
                        <td className="py-4 px-6 text-sm" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedWarehouseIds.has(w.warehouse_id)}
                            onChange={() => handleToggleWarehouse(w.warehouse_id)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                          />
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-900 font-semibold">
                          {w.warehouse_name}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600 font-mono">
                          {w.warehouse_code}
                        </td>
                        <td className="py-4 px-6 text-sm font-bold text-blue-700 text-right">
                          {w.current_quantity?.toLocaleString('en-IN', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {/* Submit Actions */}
        <div className="flex justify-end gap-4 mt-8">
          <button
            type="button"
            onClick={() => navigate('/schedule-auction')}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-xl transition shadow"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting || selectedWarehouseIds.size === 0}
          >
            {submitting ? 'Scheduling...' : 'Schedule Auction'}
          </button>
        </div>

      </form>
    </div>
  );
}
